import React, { useState, useEffect, useRef } from "react";
import Banner from "../../../components/Banner/Banner";
import AdvanceSearch from "../../../components/AdvanceSearch/AdvanceSearch";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getTopThingsToDoList, fetchAllTourPlaces } from "../../../services/restapiService";
import { destinationsData } from "../../../utils/data";
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  Map
} from "lucide-react";
import { toast } from "react-toastify";


import "./home.css";

const Home = () => {
  const navigate = useNavigate();

  const [tripDetailsData, setTripDetailsData] = useState([]);
  const [categoryGroups, setCategoryGroups] = useState({});
  const [tourPlaces, setTourPlaces] = useState([]);
  const scrollRefs = useRef({});
  const [currentSlide, setCurrentSlide] = useState({});
  const [isPlaying, setIsPlaying] = useState(true);

  const autoSlide = true;

  const slideInterval = 3000;



  // --------------------------
  // Load Display Titles
  // --------------------------
  useEffect(() => {
    const loadPlaces = async () => {
      try {
        const res = await fetchAllTourPlaces();
        const data = res.data.data;
        console.log("Fetched tour places ==== >>:", data);
        setTourPlaces(data);
      } catch (error) {
        toast.error("Failed to fetch display titles");
      }
    };
    loadPlaces();
  }, []);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await getTopThingsToDoList(token);
        const tripsRaw = response.data.data;
        console.log("Raw trips data:", tripsRaw);
        const trips = tripsRaw.map((item) => ({
          id: item.id,
          title: item.title,
          description: item.description,
          category: item.category ? item.category.trim().toLowerCase() : "",
          image: item.image
            ? `https://api.gosehari.com${item.image}`
            : "/placeholder.png",
          price: item.price,
          actualprice: item.actual_price,
          discounted_price: item.discounted_price,
          inputblock: item.inputblock,
          ctaText: item.cta_text || "View Options",
        }));

        setTripDetailsData(trips);

        // Group by category
        const grouped = trips.reduce((acc, trip) => {
          if (!acc[trip.category]) acc[trip.category] = [];
          acc[trip.category].push(trip);
          return acc;
        }, {});

        setCategoryGroups(grouped);
      } catch (err) {
        console.error("Failed to fetch trips:", err);
      }
    };

    fetchData();
  }, []);



  const slideLeft = (category, trips) => {
    setCurrentSlide((prev) => ({
      ...prev,
      [category]: Math.max((prev[category] || 0) - 1, 0),
    }));
  };

  const slideRight = (category, trips) => {
    const visible =
      window.innerWidth >= 1200
        ? 4
        : window.innerWidth >= 992
          ? 3
          : window.innerWidth >= 768
            ? 2
            : 1;

    const max = Math.max(trips.length - visible, 0);

    setCurrentSlide((prev) => ({
      ...prev,
      [category]:
        (prev[category] || 0) >= max
          ? 0
          : (prev[category] || 0) + 1,
    }));
  };


  // --------------------------
  // Helper to match Display Title
  // --------------------------
  const getDisplayTitleParts = (category) => {
    const match = tourPlaces.find(
      (item) =>
        item.category.toLowerCase().trim() === category.toLowerCase().trim()
    );

    if (!match) {
      return {
        before: "Top Things To Do ",
        category,
      };
    }

    const title = match.display_title;
    const lowerCat = category.toLowerCase();

    if (title.toLowerCase().endsWith(lowerCat)) {
      return {
        before: title.slice(0, title.toLowerCase().lastIndexOf(lowerCat)),
        category,
      };
    }

    return {
      before: title + " ",
      category,
    };
  };

  useEffect(() => {
    if (!autoSlide) return;

    const interval = setInterval(() => {
      if (!isPlaying) return;

      Object.keys(scrollRefs.current).forEach((key) => {
        const container = scrollRefs.current[key];

        if (!container) return;

        const maxScroll =
          container.scrollWidth - container.clientWidth;

        if (container.scrollLeft >= maxScroll - 20) {
          container.scrollTo({
            left: 0,
            behavior: "smooth",
          });
        } else {
          container.scrollBy({
            left: 340,
            behavior: "smooth",
          });
        }
      });
    }, slideInterval);

    return () => clearInterval(interval);

  }, [isPlaying]);

  const categoryIdMap = {};

  tourPlaces.forEach((item) => {
    categoryIdMap[item.category.trim().toLowerCase()] = item.id;
  });

  const sortedCategories = Object.entries(categoryGroups).sort(
    ([a], [b]) =>
      (categoryIdMap[a] || 9999) -
      (categoryIdMap[b] || 9999)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const next = {};

        sortedCategories.forEach(([category, trips]) => {
          const max =
            window.innerWidth >= 1200
              ? Math.max(trips.length - 4, 0)
              : window.innerWidth >= 992
                ? Math.max(trips.length - 3, 0)
                : window.innerWidth >= 768
                  ? Math.max(trips.length - 2, 0)
                  : Math.max(trips.length - 1, 0);

          next[category] =
            prev[category] >= max ? 0 : (prev[category] || 0) + 1;
        });

        return next;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [sortedCategories]);

  const handleBooking = (id) => {
    console.log("id===", id)
    navigate(`/trip-details/${id}`);
  };

  const destinationRef = useRef(null);

  const scrollDestination = (direction) => {
    if (!destinationRef.current) return;

    const cardWidth =
      window.innerWidth < 768 ? 280 : 320;

    destinationRef.current.scrollBy({
      left: direction === "left" ? -cardWidth : cardWidth,
      behavior: "smooth",
    });
  };

  return (
    <>
      <div className="banner-search-wrapper position-relative">
        <Banner />
        <div className="search-overlay">
          <AdvanceSearch />
        </div>
      </div>

      {sortedCategories.map(([category, trips]) => {
        const titleParts = getDisplayTitleParts(category);
        return (
          <section className="py-8 md:py-10 bg-bg">
            <div className="container px-4 md:px-6">
              <div className="flex items-end justify-between mb-8">

                <h2 className="text-[clamp(1.5rem,2vw,2rem)] font-bold leading-tight tracking-tight">
                  {titleParts.before}
                  <span className="text-red-600">
                    {titleParts.category}
                  </span>
                </h2>

                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center"
                >
                  {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                </button>

              </div>


              <div className="relative group">
                {/* Left Arrow */}
                <button
                  onClick={() => slideLeft(category, trips)}
                  className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-6 z-20 w-12 h-12 rounded-full bg-white shadow-xl items-center justify-center hover:bg-red-600 hover:text-white transition"
                >
                  <ChevronLeft size={22} />
                </button>

                {/* Right Arrow */}
                <button
                  onClick={() => slideRight(category, trips)}
                  className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-6 z-20 w-12 h-12 rounded-full bg-white shadow-xl items-center justify-center hover:bg-red-600 hover:text-white transition"
                >
                  <ChevronRight size={22} />
                </button>

                <div className="overflow-hidden">




                  <div
                    className="flex transition-all duration-700 ease-in-out "
                    style={{
                      transform: `translateX(-${(currentSlide[category] || 0) *
                        (100 /
                          (window.innerWidth >= 1280
                            ? 4
                            : window.innerWidth >= 1024
                              ? 3
                              : window.innerWidth >= 640
                                ? 2
                                : 1))
                        }%)`,
                    }}
                  >

                    {trips.map((trip) => (

                      <div
                        key={trip.id}
                        className="w-full sm:w-1/2 lg:w-1/3 xl:w-1/4 shrink-0 px-3 ">
                        <div
                          onClick={() => handleBooking(trip.id)}
                          className=" bg-[#fefefe] rounded-2xl shadow-mg overflow-hidden hover:shadow-2xl transition-all duration-300 h-full cursor-pointer flex flex-col group"
                        >
                          {/* Image */}
                          <div className="relative overflow-hidden">
                            <img
                              src={trip.image}
                              alt={trip.title}
                              className="w-full h-56 lg:h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                          </div>

                          {/* Content */}

                          <div className="py-6 px-2 flex flex-col flex-1 ">
                            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                              <span className="inline-flex items-center rounded-full bg-[#edf2f1] px-3 py-1 text-xs font-semibold text-[#24584C] shadow-sm capitalize">
                                {trip.category}
                              </span>

                              <span className="inline-flex items-center rounded-full bg-[#edf2f1] px-3 py-1 text-xs font-semibold text-[#24584C] whitespace-nowrap">
                                T&C'S APPLY
                              </span>
                            </div>


                            <div className="flex flex-col flex-1">

                              <h3 className="text-lg md:text-xl font-bold text-gray-900 leading-snug line-clamp-2 min-h-[56px]">
                                {trip.title}
                              </h3>

                              <div className="w-12 h-1 rounded-full bg-red-600 mt-3 mb-4"></div>

                              <p className="text-sm md:text-base text-gray-600 leading-7 line-clamp-3 flex-1 min-h-[72px]">
                                {trip.description}
                              </p>

                            </div>
                          </div>

                          {/* Footer */}
                          <div className="py-0 px-2 flex flex-col">
                            <span className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                              From
                            </span>

                            <div className="flex items-end gap-2 flex-wrap">
                              <span className="text-2xl md:text-3xl font-bold text-gray-900 leading-none">
                                ₹{trip.discounted_price}
                              </span>

                              {trip.actualprice && (
                                <>
                                  <span className="text-sm text-gray-400 line-through mb-1">
                                    ₹{trip.actualprice}
                                  </span>

                                </>
                              )}
                            </div>
                          </div>


                        </div>
                      </div>

                    ))}

                  </div>

                </div>

              </div>
            </div>
          </section>
        );
      })}






      <section className="py-16 bg-white">
        <div className="container px-4">

          <div className="flex items-center justify-between mb-8">
            <h2 className="text-4xl font-bold">
              Top Destinations For Your
              <span className="text-red-600"> Next Vacation</span>
            </h2>

            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => scrollDestination("left")}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-white border border-gray-200 transition-all duration-300 hover:bg-red-600 hover:text-white hover:shadow-xl"
                aria-label="Previous"
              >
                <ChevronLeft className="h-6 w-6" strokeWidth={2.5} />
              </button>

              <button
                onClick={() => scrollDestination("right")}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-white border border-gray-200 transition-all duration-300 hover:bg-red-600 hover:text-white hover:shadow-xl"
                aria-label="Next"
              >
                <ChevronRight className="h-6 w-6" strokeWidth={2.5} />
              </button>
            </div>


          </div>

          <div
            ref={destinationRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar pb-3"
          >
            {destinationsData.map((item) => (
              <div
                key={item.id}
                className="snap-start flex-shrink-0 w-[280px] md:w-[300px]"
              >
                <div className="group relative h-[340px] rounded-3xl overflow-hidden cursor-pointer">

                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover transition duration-500 group-hover:scale-110"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />

                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold">
                      {item.name}
                    </h3>

                    <p className="text-white/80 text-sm mt-1">
                      {item.tours}
                    </p>
                  </div>

                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

    </>

  );
};

export default Home;
